// Resolves an Apple identity that Firebase Auth cannot represent.
//
// Sign in with Apple issues a subject identifier scoped to the Apple developer
// TEAM, and our fleet spans two teams — so one person can arrive with an Apple
// identity that Firebase has never seen while their real account sits under the
// other team's identifier. A Firebase user holds only one apple.com provider
// entry, so the second identifier cannot be linked; the mapping lives on the
// server and comes back as a custom token for the account that holds the
// user's history.

const RESOLVE_PATH = '/api/v1/identity/resolve'

// This call sits between a failed Apple sign-in and the error AuthProvider
// rethrows for it, so a hung request does not merely delay a resolution --
// it leaves signInWithApple() pending forever and the user watching a
// spinner that will never resolve either way. Hence the abort timeout on
// every fetch below.
const FETCH_TIMEOUT_MS = 5000

export type AppleResolveOutcome =
  | { kind: 'resolved'; customToken: string; canonicalUid: string }
  | { kind: 'no-canonical-account' }
  | { kind: 'link-required' }
  | { kind: 'merge-required' }
  | { kind: 'unavailable' }

/**
 * The resolver base URL from runtime config, or undefined when resolution is
 * not configured for this environment.
 *
 * Read live from `window` rather than from a snapshot taken at module import,
 * matching `isFeatureEnabled` in the app's config loader: the snapshot would be
 * impossible to vary in a test and would ignore any later replacement of the
 * config object.
 *
 * Values arrive as STRINGS in a deployed environment because config.js is
 * produced by envsubst, so an unset variable becomes `""` and an unsubstituted
 * one keeps its `${...}` shape. Both mean "not configured".
 */
export function identityResolverBaseUrl(): string | undefined {
  const raw: unknown = (window as { __APP_CONFIG__?: { identityResolverUrl?: unknown } })
    .__APP_CONFIG__?.identityResolverUrl
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  if (!trimmed || trimmed.startsWith('${')) return undefined
  return trimmed
}

/**
 * Asks the resolver which account this Apple ID token belongs to.
 *
 * The token is the ONLY input: the server verifies its signature against
 * Apple's keys and reads the subject and email out of the verified claims. A
 * caller-supplied email or uid would be an account-takeover primitive, so none
 * is sent.
 *
 * Never throws — every failure becomes `unavailable`, because the caller's
 * fallback is to rethrow the original Firebase error and a resolver problem
 * must not replace it with a less accurate one.
 */
export async function resolveAppleIdentity(
  appleIdToken: string,
  baseUrl: string,
): Promise<AppleResolveOutcome> {
  try {
    // The Apple ID token is a session-minting credential at the resolver, so
    // where and how it travels matters as much as what's in the response.
    // Reject anything but https outright — an http (or otherwise unparseable)
    // configured base URL would send a live credential in the clear — and
    // never follow a redirect, so a resolver host that starts 307-ing can't
    // silently forward the token to a different, unverified origin.
    const parsedBase = new URL(baseUrl)
    if (parsedBase.protocol !== 'https:') {
      return { kind: 'unavailable' }
    }

    const response = await fetch(`${baseUrl.replace(/\/+$/, '')}${RESOLVE_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appleIdToken }),
      redirect: 'error',
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    })

    const body: unknown = await response.json().catch(() => ({}))

    if (response.status === 200) {
      const { customToken, canonicalUid } = body as {
        customToken?: unknown
        canonicalUid?: unknown
      }
      // Both must be non-empty: an empty string satisfies `typeof x ===
      // 'string'` but is not a usable token or uid, and would otherwise
      // read as a positive resolution.
      if (
        typeof customToken === 'string' &&
        customToken !== '' &&
        typeof canonicalUid === 'string' &&
        canonicalUid !== ''
      ) {
        return { kind: 'resolved', customToken, canonicalUid }
      }
      return { kind: 'unavailable' }
    }

    const error = (body as { error?: unknown }).error
    if (response.status === 404 && error === 'no_canonical_account') {
      return { kind: 'no-canonical-account' }
    }
    if (response.status === 409 && error === 'link_required') return { kind: 'link-required' }
    if (response.status === 409 && error === 'merge_required') return { kind: 'merge-required' }

    return { kind: 'unavailable' }
  } catch {
    // Offline, DNS failure, CORS, an unparseable base URL, a redirect response
    // (rejected by `redirect: 'error'`) — indistinguishable here and all
    // handled the same way by the caller, which rethrows the original
    // sign-in error.
    return { kind: 'unavailable' }
  }
}
